import type React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { useFlags } from "@/services/feature"
import { useUrl } from "@/utils/url"

import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  styled,
} from "@mui/material"
import type { PaperProps } from "@mui/material"
import type { Theme } from "@mui/material/styles"

import type { ReportCard } from "../../models/report"

const reportingCardFooterActiveStyles = (theme: Theme) => ({
  "--variant-containedBg": (theme.vars ?? theme).palette.secondary.dark,
})

const ReportingCardRoot = styled(Paper)<PaperProps>(({ theme }) => ({
  color: "inherit",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: 0,
  textDecoration: "none",
  transform: "translate3d(0, 0, 0)",
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: theme.typography.pxToRem(320),
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
  "@media (hover: hover)": {
    "&:hover .ReportingCardFooter": reportingCardFooterActiveStyles(theme),
  },
  "&:focus-visible .ReportingCardFooter":
    reportingCardFooterActiveStyles(theme),
  "@media (prefers-reduced-motion: no-preference)": {
    "&:hover, &:focus-visible": {
      boxShadow: theme.shadows[4],
      transform: `translate3d(0, -${theme.spacing(0.25)}, 0)`,
    },
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&:hover, &:focus-visible": {
      boxShadow: theme.shadows[4],
    },
  },
})) as typeof Paper

const ReportingCardBody = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}))

interface ReportingCardProps {
  card: ReportCard
}

export const ReportingCard: React.FC<ReportingCardProps> = ({ card }) => {
  const { t } = useTranslation()
  const { buildReportingUrl } = useUrl()
  const flags = useFlags()

  const isNewChipVisible: boolean = card.newFeatureFlag
    ? Boolean(flags[card.newFeatureFlag])
    : false

  const reportTitle = t(card.title, { ns: "reports" })

  const to = card.buttonLink.startsWith("/")
    ? card.buttonLink
    : buildReportingUrl(card.buttonLink)

  return (
    <ReportingCardRoot
      aria-label={t("startReportCardAriaLabel", {
        ns: "reports",
        title: reportTitle,
      })}
      component={Link}
      data-e2e={card.dataE2e ?? ""}
      data-testid={card.dataE2e}
      to={to}
    >
      <ReportingCardBody>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: card.logo ? "top" : "center",
            mb: 1.5,
          }}
        >
          {card.logo ? (
            <Box
              alt={reportTitle}
              component="img"
              src={card.logo}
              sx={{
                height: (theme) => theme.typography.pxToRem(60),
                mt: 0,
                mb: 1.5,
              }}
            />
          ) : (
            <Typography
              variant="h2"
              sx={{
                lineHeight: 1.2,
              }}
            >
              {reportTitle}
            </Typography>
          )}
          <span>
            {isNewChipVisible && (
              <Chip
                color="primary"
                label={t("chip.new", { ns: "common" })}
                size="small"
                variant="outlined"
              />
            )}
          </span>
        </Stack>
        {card.description && (
          <Typography>{t(card.description, { ns: "reports" })}</Typography>
        )}
      </ReportingCardBody>
      <Button
        aria-hidden
        className="ReportingCardFooter"
        color="secondary"
        component="span"
        disableElevation
        disableRipple
        fullWidth
        role="presentation"
        sx={{
          borderRadius: 0,
          pointerEvents: "none",
        }}
        tabIndex={-1}
        variant="contained"
      >
        {t(card.buttonText, { ns: "reports" })}
      </Button>
    </ReportingCardRoot>
  )
}
